import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import { Box, Typography } from '@mui/material';
import { debounce } from '@mui/material/utils';
import AllLPsFilters from 'features/IRWorkstation/components/AllLPs/AllLPsFilters';
import buildAllLpsGridColumns from 'features/IRWorkstation/components/AllLPs/allLpsGridColumns';
import TableCard from 'features/IRWorkstation/components/Common/TableCard';
import SearchInput from 'features/IRWorkstation/components/Search/SearchInput';
import { SEARCH_DEBOUNCE_MS } from 'features/IRWorkstation/constants/debounce';
import {
	LP_INITIAL_FILTERS,
	LP_SORT_KEYS,
} from 'features/IRWorkstation/constants/lpColumns';
import useAccounts from 'features/IRWorkstation/hooks/useAccounts';
import useFundraisingSort from 'features/IRWorkstation/hooks/useFundraisingSort';
import salesforceAccountsService from 'services/salesforceAccountsService';
import { useIRWorkstation } from 'features/IRWorkstation/contexts/IRWorkstationContext';

import { useTrackEvent } from 'hooks/useTrackEvent';
import { ButtonClickTargets } from 'constants/buttonClickTargets';
import { PageLoadsTargets } from 'constants/pageLoadTargets';
import { SearchTargets } from 'constants/searchTargets';

const SORT_STORAGE_KEY = 'allLps:sort';

const FILTER_CLICK_TARGETS = {
	investor_type: ButtonClickTargets.AllLPsInvestorTypeFilter,
	coverage_tier: ButtonClickTargets.AllLPsCoverageTierFilter,
	internal_coverage: ButtonClickTargets.AllLPsInternalCoverageFilter,
	health: ButtonClickTargets.AllLPsHealthFilter,
};

const AllLPs = () => {
	const { tenantId } = useParams();
	const { pathname } = useLocation();
	const { tenant } = useIRWorkstation();
	const { trackEvent, trackButtonClick, trackPageLoad } = useTrackEvent({
		tenant,
		product: 'IEM',
	});

	const [searchInput, setSearchInput] = useState('');
	// debounced so the API isn't hit per keystroke
	const [search, setSearch] = useState('');
	const [selectedFilters, setSelectedFilters] = useState(LP_INITIAL_FILTERS);
	const [picklistValues, setPicklistValues] = useState(null);

	const { sort, applySort } = useFundraisingSort(LP_SORT_KEYS, SORT_STORAGE_KEY);

	// stable identities, or every render refetches page 1
	const filters = useMemo(
		() => ({ ...selectedFilters, search }),
		[selectedFilters, search]
	);
	const sortParams = useMemo(
		() => ({ sortBy: sort.sortBy, sortDir: sort.sortDir }),
		[sort.sortBy, sort.sortDir]
	);

	const {
		accounts,
		totalCount,
		assignedToMe,
		loading,
		loadingMore,
		hasMore,
		error,
		loadMore,
	} = useAccounts(filters, sortParams);

	const debouncedSetSearch = useMemo(
		() => debounce(setSearch, SEARCH_DEBOUNCE_MS),
		[]
	);
	// SEARCH event, not a button click; the term is passed at call time to avoid a stale closure
	const debouncedSearchTracking = useMemo(
		() =>
			debounce(
				(term) =>
					trackEvent('IEM', 'SEARCH', SearchTargets.AllLPsSearch, 'log', 'created', {
						changed_data: { search: term },
					}),
				1000
			),
		[trackEvent]
	);

	useEffect(
		() => () => {
			debouncedSetSearch.clear();
			debouncedSearchTracking.clear();
		},
		[debouncedSetSearch, debouncedSearchTracking]
	);

	useEffect(() => {
		trackPageLoad(PageLoadsTargets.AllLPs);
	}, []);

	const cardRef = useRef(null);

	// keep fetching until the grid scrolls — a tall screen would otherwise stall on page 1
	useEffect(() => {
		const scroller = cardRef.current?.querySelector(
			'.MuiDataGrid-virtualScroller'
		);

		if (
			scroller &&
			!error &&
			scroller.scrollHeight <= scroller.clientHeight
		) {
			loadMore();
		}
	}, [accounts.length, error, loadMore]);

	// a failed load leaves the dropdowns with just their "All" option
	useEffect(() => {
		let cancelled = false;

		salesforceAccountsService
			.getPicklistValues()
			.then((data) => {
				if (!cancelled) setPicklistValues(data);
			})
			.catch((err) => {
				console.error('Failed to load picklist values:', err);
			});

		return () => {
			cancelled = true;
		};
	}, []);

	const handleSearchChange = useCallback(
		(event) => {
			setSearchInput(event.target.value);
			debouncedSetSearch(event.target.value);
			debouncedSearchTracking(event.target.value);
		},
		[debouncedSetSearch, debouncedSearchTracking]
	);

	const handleFilterChange = useCallback(
		(key, value) => {
			trackButtonClick(FILTER_CLICK_TARGETS[key], {
				changed_data: { [key]: value },
			});
			setSelectedFilters((current) => ({ ...current, [key]: value }));
		},
		[trackButtonClick]
	);

	// cancel the debounce so a pending keystroke can't restore the cleared term
	const handleReset = useCallback(() => {
		trackButtonClick(ButtonClickTargets.AllLPsResetFilters);
		debouncedSetSearch.clear();
		debouncedSearchTracking.clear();
		setSelectedFilters(LP_INITIAL_FILTERS);
		setSearchInput('');
		setSearch('');
	}, [debouncedSetSearch, debouncedSearchTracking, trackButtonClick]);

	// LP360 renders this as its back breadcrumb
	const getAccountLink = useCallback(
		(row) => ({
			to: `/tenants/${tenantId}/workstation/lps/${row.id}`,
			state: {
				from: {
					label: 'LP Dashboard',
					pathname,
					backTarget: ButtonClickTargets.LP360BackToAllLPs,
				},
			},
			onClick: () =>
				trackButtonClick(ButtonClickTargets.AllLPsAccountSelection),
		}),
		[tenantId, pathname, trackButtonClick]
	);

	const columns = useMemo(
		() => buildAllLpsGridColumns(getAccountLink),
		[getAccountLink]
	);

	const sortModel = useMemo(
		() => (sort.sortBy ? [{ field: sort.sortBy, sort: sort.sortDir }] : []),
		[sort.sortBy, sort.sortDir]
	);

	const hasActiveFilters =
		Object.values(selectedFilters).some((value) => value !== '') ||
		search !== '';
	const noRowsMessage = hasActiveFilters
		? 'No LPs match the selected filters'
		: 'No LPs found';

	return (
		<Box
			sx={{
				flex: 1,
				minWidth: 0,
				minHeight: 0,
				display: 'flex',
				flexDirection: 'column',
				overflowY: 'auto',
				padding: '16px 20px',
				paddingBottom: '24px',
				backgroundColor: 'background.default',
				gap: '14px',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: '12px',
				}}
			>
				<Box>
					<Typography
						variant='h1'
						sx={{ fontSize: '17px', fontWeight: 700 }}
					>
						LP Dashboard
					</Typography>
					{assignedToMe != null && (
						<Typography
							data-testid='all-lps-assigned-count'
							sx={{ fontSize: '12px', color: 'text.secondary' }}
						>
							{`${assignedToMe} assigned to you`}
						</Typography>
					)}
				</Box>
				<SearchInput
					data-testid='all-lps-search-input'
					value={searchInput}
					onChange={handleSearchChange}
					placeholder='Search LPs…'
				/>
			</Box>

			<Box
				ref={cardRef}
				sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
			>
				<TableCard
					totalCount={totalCount}
					totalLabel='records'
					cardTestId='all-lps-card'
					filterBar={
						<AllLPsFilters
							filters={selectedFilters}
							options={picklistValues}
							onChange={handleFilterChange}
							onReset={handleReset}
							hasSearch={search !== ''}
						/>
					}
					columns={columns}
					rows={accounts}
					loading={loading}
					loadingMore={loadingMore}
					hasMore={hasMore}
					error={error}
					errorMessage='Failed to load LPs'
					sortModel={sortModel}
					onSortModelChange={applySort}
					onRowsScrollEnd={loadMore}
					noRowsMessage={noRowsMessage}
					dataTestId='all-lps-grid'
					height='100%'
				/>
			</Box>
		</Box>
	);
};

export default AllLPs;
